import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DESC } from 'framework/list';
import type { FilterState } from '../../contracts/filter';
import { TIMELINE_ENTRY_ID_PARAM } from './constants';
import type { TimelineRecordId } from './types/types';
import useCollectionPager from '../../hooks/collection/useCollectionPager';
import type {
  UseCollectionPagerParams,
  FetchCollectionPageParams,
  CollectionPageResponse,
} from '../../hooks/collection/collectionPagerTypes';

export type UseTimelinePagerParams<Item extends TimelineRecordId, Filters = FilterState> = Omit<
  UseCollectionPagerParams<Item, Filters>,
  'fetchInitialPage' | 'pageSize'
> & {
  pageSize?: number;
  fetchEntryPage?: (
    entryId: string,
    params: FetchCollectionPageParams<Filters>,
  ) => Promise<CollectionPageResponse<Item>>;
};

const useTimelinePager = <Item extends TimelineRecordId, Filters = FilterState>({
  viewParamsConfig,
  pageSize = 30,
  fetchEntryPage,
  fetchPage,
  collectionKey,
  ...params
}: UseTimelinePagerParams<Item, Filters>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const entryId = searchParams.get(TIMELINE_ENTRY_ID_PARAM) ?? undefined;
  const config = useMemo(
    () => ({
      ...viewParamsConfig,
      cache: viewParamsConfig.cache ?? false,
      cacheKey: viewParamsConfig.cacheKey ?? 'timeline',
      syncWithUrl: viewParamsConfig.syncWithUrl ?? false,
      filterParam: viewParamsConfig.filterParam ?? 'timelineFilter',
      sortByParam: viewParamsConfig.sortByParam ?? 'timelineSortBy',
      sortOrderParam: viewParamsConfig.sortOrderParam ?? 'timelineSortOrder',
      defaultSortBy: viewParamsConfig.defaultSortBy ?? 'createdAt',
      defaultSortOrder: viewParamsConfig.defaultSortOrder ?? DESC,
    }),
    [viewParamsConfig],
  );
  const isEntryViewMode = Boolean(entryId && fetchEntryPage);
  const withEntryViewDefaults = (request: FetchCollectionPageParams<Filters>) =>
    isEntryViewMode
      ? {
          ...request,
          filters: config.defaultFilters ?? ({} as Filters),
          sort:
            config.defaultSortBy && config.defaultSortOrder
              ? { [config.defaultSortBy]: config.defaultSortOrder }
              : undefined,
        }
      : request;
  const pager = useCollectionPager<Item, Filters>({
    ...params,
    pageSize,
    viewParamsConfig: config,
    collectionKey: entryId ? `${collectionKey ?? ''}:timeline-entry:${entryId}` : collectionKey,
    fetchPage: request => fetchPage(withEntryViewDefaults(request)),
    fetchInitialPage:
      entryId && fetchEntryPage ? request => fetchEntryPage(entryId, withEntryViewDefaults(request)) : undefined,
  });
  const { resetFilters: resetPagerFilters, setFilters: setPagerFilters } = pager;

  const resetEntryView = () => {
    if (!entryId) return;

    setSearchParams(
      currentSearchParams => {
        const nextSearchParams = new URLSearchParams(currentSearchParams);
        nextSearchParams.delete(TIMELINE_ENTRY_ID_PARAM);
        return nextSearchParams;
      },
      { replace: true },
    );
  };

  const setFilters: typeof setPagerFilters = filters => {
    setPagerFilters(filters);
    resetEntryView();
  };

  const resetFilters = () => {
    resetPagerFilters();
    resetEntryView();
  };

  return {
    ...pager,
    items: pager.rows,
    entryId,
    isEntryViewMode,
    setFilters,
    resetFilters,
  };
};

export default useTimelinePager;
